import { UserOutlined } from '@ant-design/icons';
import { getImageUrlFromStorage } from 'library/helpers/getImageUrlFromStorage';
import React, { useEffect, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { useSelector } from 'react-redux';
import { Avatar } from 'zui/Antd';
import { Loader } from '..';
import ProfilePicUpload from '../UploadImageFirebase';
import ProfileImageWrapper from './styles';

/**
 * Safe initials extractor: "John Doe" -> "JD"
 */
const getInitials = (fullName = '') => {
	const trimmed = fullName?.trim();
	if (!trimmed) return '';
	const parts = trimmed.split(/\s+/);
	if (parts.length === 1) return parts[0][0].toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
};

const PersonProfileImage = ({
	entityID,
	photoUrl,
	size = 150,
	entityName,
	shape = 'square',
	cursor,
	edit,
	uploadRef,
	name = ''
}) => {
	const { loadingPhotoProfile } = useSelector((state) => state.People);
	const [image, setImage] = useState(null);
	const [loading, setLoading] = useState(true);
	const initials = getInitials(name);

	useEffect(() => {
		let mounted = true;
		const fetchImage = async () => {
			setLoading(true);
			if (photoUrl) {
				try {
					const url = await getImageUrlFromStorage(photoUrl);
					if (mounted) setImage(url);
				} catch {
					if (mounted) setImage(null);
				}
			} else {
				if (mounted) setImage(null);
			}
			if (mounted) setLoading(false);
		};
		fetchImage();
		return () => {
			mounted = false;
		};
	}, [photoUrl, loadingPhotoProfile]);

	const avatarStyle = {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		cursor: cursor ? 'pointer' : 'default',
		lineHeight: `${size}px`,
		textAlign: 'center'
	};

	const renderAvatar = () => {
		if (loading) {
			return <Avatar size={size} style={avatarStyle} icon={<Loader />} />;
		}

		if (image) {
			return (
				<div
					style={{
						width: size,
						height: size,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						overflow: 'hidden',
						borderRadius: shape === 'circle' ? '50%' : 4
					}}
				>
					<LazyLoadImage
						src={image}
						effect='blur'
						loading='lazy'
						style={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
				</div>
			);
		}

		// No image: show initials or fallback icon
		return (
			<Avatar
				size={size || 42}
				shape={shape}
				style={{
					...avatarStyle,
					backgroundColor: '#D9D9D9',
					color: '#000',
					fontWeight: 500
				}}
			>
				{initials || <UserOutlined />}
			</Avatar>
		);
	};

	return (
		<ProfileImageWrapper size={size} shape={shape} cursor={cursor ? 'pointer' : 'default'}>
			{edit !== false ? (
				<ProfilePicUpload
					cropperOptions={{ grid: true, rotate: true }}
					entityName={entityName}
					entityID={entityID}
					uploadType='profilePicture'
					oldData={{ photoUrl }}
					uploadRef={uploadRef}
				>
					<div className='contentContainer'>
						<div className='editIconContainer'>
							<p className='editIconContent'>
								{image ? 'Update' : 'Upload'}
								<br />
								Picture
							</p>
						</div>
						<div className='imageLazy'>{renderAvatar()}</div>
					</div>
				</ProfilePicUpload>
			) : (
				<span className='imageLazy'>{renderAvatar()}</span>
			)}
		</ProfileImageWrapper>
	);
};

export default PersonProfileImage;
